/**
 * One colour scheme for court events, everywhere they appear.
 *
 * This lived inside CalendarEditable, which meant the calendar Kelsi works from and every other
 * place a court date is shown agreed by coincidence rather than by construction — and they did not
 * agree. Lloyd, Aug 18: "For court dates, can you color code them to match what Kelsi has for the
 * Calendar view?" Moving the function here is the whole of that change; the calendar keeps its
 * exact colours and the rest of the app now borrows them instead of inventing its own.
 *
 * ## Colour by COURT, not by hearing type (19 Sep meeting, and Kelsi's email of 24 Sep)
 *
 * This used to read the title for a hearing type — pretrial purple, docket teal. Lloyd asked for the
 * other axis: "we can do it by the type of court that we're using instead of the type [of event]...
 * each trial needs to be all one color." Ashley agreed "with the exception of jury trials", because
 * "a jury or a bench trial, we're going to have to go no matter what". Kelsi then gave the colours.
 *
 * ## The category is decided on the server
 *
 * `CourtEvent_CalendarCategory` arrives already worked out, from
 * `backend/utils/courtEvents/calendarCategory.ts`. Nothing here reads a title. Re-deriving "is this
 * a jury trial" in the browser would put the same regexes on both sides of an API, and that is how a
 * feature ends up shipping correct on one side only.
 *
 * ⚠️ The field is optional because a browser can be a build ahead of the API it is talking to, and an
 * absent category draws the neutral colour rather than throwing. That failure is SILENT and looks
 * identical to a calendar of events with no court on record: during the minutes between the two
 * containers restarting, every event reads slate. It resolves itself once the API is up, and the
 * alternative — a required field — has already taken a whole page down here once.
 */

import { CalendarCategory } from '../api/CourtEventApi';

export type { CalendarCategory };

/**
 * Kelsi's five, her two overrides, and three that are mine.
 *
 * Hers, verbatim (24 Sep): "For us it would be easier to have it colored by court unless it's a Jury
 * Trial (red) or Virtual (blue)... JP courts should be purple, County needs to be orange, Municipal
 * pink, District court - green. If we can have a different color for personal/probate courts that
 * would be great."
 *
 * Mine, and she is told rather than left to notice: probate's actual shade, grey for reminders
 * (a "10 Day Warning" is not a court date), a separate colour for deadlines (a date to meet is not a
 * setting to attend), and a neutral for the events where nothing on record says which court.
 *
 * ⚠️ JP purple sits close to the `time_off` purple on the same calendar. Raised with her; if it
 * reads badly in a packed month view, one of the two moves.
 *
 * Solid 700-level so white text clears WCAG AA, which is the constraint the filled pills impose.
 */
export const CALENDAR_CATEGORY_COLORS: Record<CalendarCategory, string> = {
  trial: '#c62828',
  virtual: '#1565c0',
  jp: '#6a1b9a',
  county: '#ef6c00',
  municipal: '#ad1457',
  district: '#2e7d32',
  probate: '#00695c',
  reminder: '#5f6368',
  deadline: '#827717',
  unknown: '#455a64',
};

/**
 * The legend, in the order it reads on screen: what wins first, then the courts.
 *
 * Derived from the map above rather than written out again, so a colour cannot be changed in one
 * place and explained in another.
 */
export const COURT_EVENT_COLOR_KEY: { label: string; color: string }[] = (
  [
    ['trial', 'Jury or bench trial'],
    ['virtual', 'Virtual'],
    ['jp', 'Justice of the peace'],
    ['county', 'County'],
    ['municipal', 'Municipal'],
    ['district', 'District'],
    ['probate', 'Probate'],
    ['reminder', 'Reminder'],
    ['deadline', 'Deadline'],
    ['unknown', 'No court recorded'],
  ] as [CalendarCategory, string][]
).map(([category, label]) => ({ label, color: CALENDAR_CATEGORY_COLORS[category] }));

export function courtEventColor(category: string | null | undefined): string {
  return CALENDAR_CATEGORY_COLORS[(category ?? 'unknown') as CalendarCategory] ??
    CALENDAR_CATEGORY_COLORS.unknown;
}
